import React, { useState, FormEvent } from 'react';
import styled from 'styled-components';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

// Reuse color scheme from App.tsx
const COLORS = {
  primary: '#fbf2ed', // Header color
  secondary: '#d0d7c9', // Button color
  accent: '#d0d7c9', // Accent color (using same as buttons)
  light: '#f8f9fa', // Light background
  dark: '#555555', // Dark text
  lightText: '#555555', // Updated for readability on light backgrounds
  border: '#dee2e6',
  hover: '#c2c9bc', // Darker button color for hover states
  success: '#28a745',
  error: '#dc3545',
  gray: '#6c757d',
  lightGray: '#e9ecef',
};

const FormContainer = styled.div`
  max-width: 400px;
  margin: 100px auto;
  padding: 30px;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const Title = styled.h2`
  margin: 0 0 24px;
  font-size: 24px;
  font-weight: 600;
  color: ${COLORS.dark};
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${COLORS.gray};
`;

const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid ${COLORS.border};
  border-radius: 4px;
  font-size: 14px;
  color: #555555;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:focus {
    outline: none;
    border-color: ${COLORS.accent};
    box-shadow: 0 0 0 2px rgba(40, 181, 181, 0.2);
  }
`;

const SubmitButton = styled.button`
  margin-top: 8px;
  padding: 12px;
  background-color: ${COLORS.primary};
  color: #555555;
  border: none;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${COLORS.hover};
  }

  &:disabled {
    background-color: ${COLORS.gray};
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  padding: 10px;
  background-color: #ffeaea;
  border: 1px solid ${COLORS.error};
  border-radius: 4px;
  color: ${COLORS.error};
  font-size: 14px;
  margin-bottom: 16px;
`;

const LoginLink = styled.p`
  text-align: center;
  margin-top: 16px;
  font-size: 14px;
  color: ${COLORS.gray};

  a {
    color: ${COLORS.accent};
    text-decoration: none;
    font-weight: 500;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const Signup: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();

    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }

    try {
      setError('');
      setLoading(true);
      await signup(email, password);
      // Redirect to the planner after successful signup
      navigate('/');
    } catch (err) {
      setError('Failed to create an account. Email may already be in use.');
      console.error(err);
    }

    setLoading(false);
  }

  return (
    <FormContainer>
      <Title>Create Account</Title>
      {error && <ErrorMessage>{error}</ErrorMessage>}
      
      <Form onSubmit={handleSubmit}>
        <FormGroup>
          <Label htmlFor="email">Email</Label>
          <Input
            type="email"
            id="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="password">Password</Label>
          <Input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            minLength={6}
          />
        </FormGroup>
        
        <FormGroup>
          <Label htmlFor="confirm-password">Confirm Password</Label>
          <Input
            type="password"
            id="confirm-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            minLength={6}
          />
        </FormGroup>
        
        <SubmitButton disabled={loading} type="submit">
          {loading ? 'Creating Account...' : 'Sign Up'}
        </SubmitButton>
      </Form>
      
      <LoginLink>
        Already have an account? <a href="/login">Log In</a>
      </LoginLink>
    </FormContainer>
  );
};

export default Signup; 